import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Heart, AlertCircle, Gift, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import SizeSelector from './SizeSelector';

export const CakeCustomizer = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, loading } = useCart();
  // Filter out invalid sizes/flavors (empty id or name) saved accidentally via admin.
  // Radix's <SelectItem> throws if value is an empty string, which crashes the page.
  const validSizes = (product.sizes || []).filter((s) => s && s.id && s.name);
  const validFlavors = (product.flavors || []).filter((f) => f && f.id && f.name);
  const [selectedSize, setSelectedSize] = useState(validSizes[0]?.id || null);
  const [selectedFlavor, setSelectedFlavor] = useState(validFlavors[0]?.id || null);
  const [addGiftNote, setAddGiftNote] = useState(false);
  const [giftNote, setGiftNote] = useState('');
  const [addCakeMessage, setAddCakeMessage] = useState(false);
  const [cakeMessage, setCakeMessage] = useState('');
  const [addSpecialRequest, setAddSpecialRequest] = useState(false);
  const [specialRequest, setSpecialRequest] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(product.base_price);

  // Custom text template (e.g. age picker for Bento cakes)
  const customTpl = product.custom_text_template;
  const customTplEnabled = !!customTpl?.enabled && (customTpl.template || '').includes('{value}');
  const initialCustomValue = customTplEnabled
    ? (customTpl.mode === 'list'
        ? (customTpl.options?.[0] || '')
        : customTpl.mode === 'text'
          ? ''
          : String(customTpl.min ?? 21))
    : '';
  const [customTextValue, setCustomTextValue] = useState(initialCustomValue);
  const customTextFull = customTplEnabled
    ? (customTpl.template || '').replace(/\{value\}/g, customTextValue || '')
    : '';

  const allergenColors = {
    gluten: 'bg-amber-100 text-amber-800 border-amber-200',
    eggs: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    dairy: 'bg-blue-100 text-blue-800 border-blue-200',
    nuts: 'bg-orange-100 text-orange-800 border-orange-200',
  };

  useEffect(() => {
    let price = product.base_price;

    if (selectedSize && validSizes.length) {
      const size = validSizes.find(s => s.id === selectedSize);
      if (size) price += size.price_modifier || 0;
    }

    if (selectedFlavor && validFlavors.length) {
      const flavor = validFlavors.find(f => f.id === selectedFlavor);
      if (flavor) price += flavor.price_modifier || 0;
    }

    setTotalPrice(price * quantity);
  }, [selectedSize, selectedFlavor, quantity, product, validSizes, validFlavors]);

  const stock = product.stock_quantity;
  const isSoldOut = stock !== null && stock !== undefined && stock <= 0;
  const isLowStock = stock !== null && stock !== undefined && stock > 0 && stock <= 3;

  const handleAddToCart = async () => {
    if (isSoldOut) {
      toast.error('This product is sold out.');
      return;
    }
    if (customTplEnabled && !customTextValue) {
      toast.error(customTpl.label || 'Please choose a value');
      return;
    }
    try {
      await addToCart({
        product_id: product.id,
        quantity,
        size_id: selectedSize,
        flavor_id: selectedFlavor,
        gift_note: addGiftNote ? giftNote : null,
        cake_message: addCakeMessage ? cakeMessage : null,
        special_request: addSpecialRequest ? specialRequest : null,
        custom_text_value: customTplEnabled ? customTextValue : null,
        custom_text_full: customTplEnabled ? customTextFull : null,
      });
      toast.success(`${product.name} added to cart!`, {
        description: `€${totalPrice.toFixed(2)} total`,
        action: {
          label: 'View Cart',
          onClick: () => navigate('/cart'),
        },
        duration: 5000,
        style: { cursor: 'pointer' },
        onClick: () => navigate('/cart'),
      });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to add to cart';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6" data-testid="cake-customizer">
      {/* Product Title & Price */}
      <div>
        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
          {product.name}
        </h1>
        <p className="text-stone-600 mt-2">{product.description}</p>
        
        {/* Allergens */}
        {product.allergens?.length > 0 && (
          <div className="flex items-center gap-2 mt-4">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-stone-500">Contains:</span>
            <div className="flex gap-1.5">
              {product.allergens.map((allergen) => (
                <Badge
                  key={allergen}
                  variant="outline"
                  className={`text-xs ${allergenColors[allergen] || 'bg-stone-100'}`}
                >
                  {allergen}
                </Badge>
              ))}
            </div>
            <a 
              href="/allergens" 
              className="text-sm text-[#8D6E63] hover:underline ml-2"
              data-testid="allergen-link"
            >
              Full Guide →
            </a>
          </div>
        )}
      </div>

      {/* Size Selector — visual cards (round / square / cupcake box) */}
      {validSizes.length > 0 && (
        <SizeSelector
          sizes={validSizes}
          basePrice={product.base_price}
          value={selectedSize}
          onChange={setSelectedSize}
          label={product.size_label || 'Choose Your Size'}
          variant={product.size_variant || 'cake'}
        />
      )}

      {/* Flavor Selector */}
      {validFlavors.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#1a1a1a]">Flavour</Label>
          <Select value={selectedFlavor} onValueChange={setSelectedFlavor}>
            <SelectTrigger className="w-full" data-testid="flavor-select">
              <SelectValue placeholder="Select flavour" />
            </SelectTrigger>
            <SelectContent>
              {validFlavors.map((flavor) => (
                <SelectItem key={flavor.id} value={flavor.id} data-testid={`flavor-${flavor.id}`}>
                  {flavor.name}
                  {flavor.price_modifier > 0 && (
                    <span className="text-stone-500 ml-2">+€{flavor.price_modifier}</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Quantity */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-[#1a1a1a]">Quantity</Label>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="rounded-full"
            data-testid="qty-decrease"
          >
            -
          </Button>
          <span className="w-12 text-center text-lg font-medium" data-testid="quantity-display">{quantity}</span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity(quantity + 1)}
            className="rounded-full"
            data-testid="qty-increase"
          >
            +
          </Button>
        </div>
      </div>

      {/* Custom Text (e.g. Bento age picker) — admin-defined locked phrase with one variable */}
      {customTplEnabled && (
        <div className="space-y-3 p-4 bg-gradient-to-br from-[#FAF8F5] to-stone-50 rounded-xl border border-stone-200" data-testid="custom-text-section">
          <Label className="flex items-center gap-2 text-sm font-semibold text-[#1a1a1a]">
            <Gift className="w-4 h-4 text-[#D4AF37]" />
            {customTpl.label || 'Choose your value'}
          </Label>

          {customTpl.mode === 'list' ? (
            <Select value={customTextValue} onValueChange={setCustomTextValue}>
              <SelectTrigger data-testid="custom-text-select">
                <SelectValue placeholder="Pick one" />
              </SelectTrigger>
              <SelectContent>
                {(customTpl.options || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : customTpl.mode === 'text' ? (
            <>
              <input
                type="text"
                maxLength={customTpl.max_length ?? 20}
                value={customTextValue}
                onChange={(e) => setCustomTextValue(e.target.value)}
                className="w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
                data-testid="custom-text-input"
                placeholder={`Type here (up to ${customTpl.max_length ?? 20} letters)`}
              />
              <div className="flex justify-end -mt-1">
                <p className={`text-[11px] tabular-nums ${
                  (customTextValue?.length || 0) >= (customTpl.max_length ?? 20)
                    ? 'text-red-600 font-semibold' : 'text-stone-500'
                }`} data-testid="custom-text-input-counter">
                  {customTextValue?.length || 0}/{customTpl.max_length ?? 20}
                </p>
              </div>
            </>
          ) : (
            <input
              type="number"
              min={customTpl.min ?? 1}
              max={customTpl.max ?? 99}
              value={customTextValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') { setCustomTextValue(''); return; }
                const n = parseInt(v, 10);
                if (isNaN(n)) return;
                const clamped = Math.min(Math.max(n, customTpl.min ?? 1), customTpl.max ?? 99);
                setCustomTextValue(String(clamped));
              }}
              className="w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
              data-testid="custom-text-number"
              placeholder={`Between ${customTpl.min ?? 1} and ${customTpl.max ?? 99}`}
            />
          )}

          {/* Real-time preview of full phrase */}
          <div className="relative rounded-xl border-2 border-[#D4AF37] bg-gradient-to-br from-[#FFF8E1] via-[#FFF3C4] to-[#FFE4A0] px-5 py-4 shadow-sm overflow-hidden">
            <div className="absolute top-2 right-3 text-2xl opacity-30 select-none">🎂</div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8D6E63] font-semibold mb-1.5 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse"></span>
              Your cake will say
            </p>
            <p className="font-handwritten text-3xl text-[#1a1a1a] leading-tight" data-testid="custom-text-preview">
              {customTextFull || <span className="text-stone-400 italic text-xl">(pick a value above)</span>}
            </p>
          </div>
        </div>
      )}

      {/* Cake Top Message — admin-controlled */}
      {product.allow_cake_message !== false && (
        <div className="space-y-3 p-4 bg-stone-50 rounded-xl" data-testid="cake-message-section">
          <div className="flex items-center gap-3">
            <Checkbox
              id="cake-message"
              checked={addCakeMessage}
              onCheckedChange={setAddCakeMessage}
              data-testid="cake-message-checkbox"
            />
            <Label htmlFor="cake-message" className="flex items-center gap-2 cursor-pointer">
              <Gift className="w-4 h-4 text-[#8D6E63]" />
              <span>Cake Top Message <span className="text-xs text-stone-500 font-normal">— message piped on the cake</span></span>
            </Label>
          </div>

          {addCakeMessage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Textarea
                placeholder="e.g. Happy 30th Sarah!"
                value={cakeMessage}
                onChange={(e) => {
                  const max = product.cake_message_max_length ?? 50;
                  if (e.target.value.length <= max) setCakeMessage(e.target.value);
                }}
                maxLength={product.cake_message_max_length ?? 50}
                className="mt-3 font-handwritten text-lg"
                rows={2}
                data-testid="cake-message-input"
              />
              <div className="flex justify-between items-center mt-1.5 px-1">
                <p className="text-[11px] text-stone-500">
                  Short messages only (name + occasion).
                </p>
                <p className={`text-[11px] tabular-nums ${
                  cakeMessage.length >= (product.cake_message_max_length ?? 50)
                    ? 'text-red-600 font-semibold' : 'text-stone-500'
                }`} data-testid="cake-message-counter">
                  {cakeMessage.length}/{product.cake_message_max_length ?? 50}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Special Request — admin-controlled, with disclaimer */}
      {product.allow_special_request !== false && (
        <div className="space-y-3 p-4 bg-amber-50/50 border border-amber-100 rounded-xl" data-testid="special-request-section">
          <div className="flex items-center gap-3">
            <Checkbox
              id="special-request"
              checked={addSpecialRequest}
              onCheckedChange={setAddSpecialRequest}
              data-testid="special-request-checkbox"
            />
            <Label htmlFor="special-request" className="flex items-center gap-2 cursor-pointer">
              <AlertCircle className="w-4 h-4 text-amber-700" />
              <span>Special Request <span className="text-xs text-stone-500 font-normal">— extra decoration / dietary / preference</span></span>
            </Label>
          </div>

          {addSpecialRequest && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Textarea
                placeholder=""
                value={specialRequest}
                onChange={(e) => {
                  const max = product.special_request_max_length ?? 250;
                  if (e.target.value.length <= max) setSpecialRequest(e.target.value);
                }}
                maxLength={product.special_request_max_length ?? 250}
                className="mt-3 text-sm"
                rows={3}
                data-testid="special-request-input"
              />
              <div className="flex justify-between items-center mt-1.5 px-1">
                <p className="text-[11px] text-amber-700 italic">
                  Note: not all requests can be accommodated.
                </p>
                <p className={`text-[11px] tabular-nums ${
                  specialRequest.length >= (product.special_request_max_length ?? 250)
                    ? 'text-red-600 font-semibold' : 'text-stone-500'
                }`} data-testid="special-request-counter">
                  {specialRequest.length}/{product.special_request_max_length ?? 250}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Collection Info */}
      <div className="flex items-center gap-2 text-sm text-stone-600 bg-[#8D6E63]/10 p-3 rounded-lg">
        <Store className="w-4 h-4 text-[#8D6E63]" />
        <span>Free collection from our Dublin 7 bakery</span>
      </div>

      {/* Stock status */}
      {isSoldOut && (
        <div
          className="flex items-center gap-3 bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-xl"
          data-testid="customizer-soldout"
        >
          <AlertCircle className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-bold">Currently Sold Out</p>
            <p className="text-sm">We've run out of this item. Get in touch and we'll let you know when it's back.</p>
          </div>
        </div>
      )}
      {isLowStock && !isSoldOut && (
        <div
          className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm"
          data-testid="customizer-lowstock"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>
            Hurry — only <strong>{stock}</strong> left in stock!
          </span>
        </div>
      )}

      {/* Total & Add to Cart */}
      <div className="flex items-center justify-between pt-4 border-t border-stone-200">
        <div>
          <span className="text-sm text-stone-500">Total</span>
          <p className="text-3xl font-bold text-[#1a1a1a]" data-testid="total-price">
            €{totalPrice.toFixed(2)}
          </p>
        </div>
        <Button
          size="lg"
          className={`text-white rounded-full px-8 btn-shine ${
            isSoldOut
              ? 'bg-stone-400 hover:bg-stone-400 cursor-not-allowed'
              : 'bg-[#1a1a1a] hover:bg-[#1a1a1a]/90'
          }`}
          onClick={handleAddToCart}
          disabled={loading || isSoldOut}
          data-testid="add-to-cart-btn"
        >
          <ShoppingBag className="w-5 h-5 mr-2" />
          {isSoldOut ? 'Sold out' : loading ? 'Adding...' : 'Add to Basket'}
        </Button>
      </div>
    </div>
  );
};

export default CakeCustomizer;
